import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Layout } from '../components/layout/Layout';
import { QualificationBadge } from '../components/ui/Badge';
import { TripDetailModal } from '../components/trips/TripDetailModal';
import { SmartcarPanel } from '../components/smartcar/SmartcarPanel';
import { vehiclesApi, OBDHealth } from '../api/vehicles.api';
import { tripsApi } from '../api/trips.api';
import { Vehicle } from '../types/vehicle';
import { Trip } from '../types/trip';
import { User } from '../types/user';
import { useAuth } from '../hooks/useAuth';
import { formatDate, formatTime, formatDuration, formatDistance } from '../utils/formatters';
import { OdometerDisplay } from '../components/ui/OdometerDisplay';

// ---------------------------------------------------------------------------
// OBD Health sub-components (colour-coded values)
// ---------------------------------------------------------------------------

const MetricRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
    <span className="text-sm text-gray-500">{label}</span>
    <span className="text-sm font-semibold ml-2 text-right">{children}</span>
  </div>
);

const BatteryValue: React.FC<{ voltage: number | null }> = ({ voltage }) => {
  if (voltage == null) return <span className="text-gray-400">N/A</span>;
  const color = voltage >= 13.5 ? 'text-green-600' : voltage >= 12 ? 'text-amber-600' : 'text-red-600';
  return <span className={color}>{voltage.toFixed(1)} V</span>;
};

const CoolantValue: React.FC<{ temp: number | null }> = ({ temp }) => {
  if (temp == null) return <span className="text-gray-400">N/A</span>;
  const color = temp < 50 ? 'text-gray-400' : temp <= 105 ? 'text-green-600' : 'text-red-600';
  return <span className={color}>{temp} °C</span>;
};

const OilTempValue: React.FC<{ temp: number | null }> = ({ temp }) => {
  if (temp == null) return <span className="text-gray-400">N/A</span>;
  const color = temp > 130 ? 'text-red-600' : 'text-green-600';
  return <span className={color}>{temp} °C</span>;
};

const FuelBar: React.FC<{ level: number | null }> = ({ level }) => {
  if (level == null) return <span className="text-gray-400">N/A</span>;
  const pct = Math.max(0, Math.min(100, level));
  const barColor = pct < 10 ? 'bg-red-500' : pct < 25 ? 'bg-amber-400' : 'bg-green-500';
  const textColor = pct < 10 ? 'text-red-600' : pct < 25 ? 'text-amber-600' : 'text-green-600';
  return (
    <span className="flex items-center gap-2 justify-end">
      <span className={`font-bold ${textColor}`}>{pct}%</span>
      <span className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden inline-block flex-shrink-0">
        <span className={`h-full rounded-full block ${barColor}`} style={{ width: `${pct}%` }} />
      </span>
    </span>
  );
};

const DtcValue: React.FC<{ dtcCount: number | null; dtcCodes: string | null }> = ({
  dtcCount,
  dtcCodes,
}) => {
  if (dtcCount == null) return <span className="text-gray-400">N/A</span>;
  if (dtcCount === 0) return <span className="text-green-600 font-semibold">OK</span>;
  return (
    <span className="inline-flex items-center gap-1">
      <span className="bg-red-100 text-red-700 text-xs font-bold px-1.5 py-0.5 rounded-full">
        {dtcCount}
      </span>
      {dtcCodes && (
        <span className="text-red-600 font-mono text-xs truncate max-w-[100px]" title={dtcCodes}>
          {dtcCodes}
        </span>
      )}
    </span>
  );
};

const ServiceDistanceValue: React.FC<{ km: number | null }> = ({ km }) => {
  if (km == null) return <span className="text-gray-400">N/A</span>;
  const color = km < 500 ? 'text-red-600' : km < 2000 ? 'text-amber-600' : 'text-green-600';
  return <span className={color}>{km.toLocaleString('fr-FR')} km</span>;
};

// ---------------------------------------------------------------------------
// Skeleton loader
// ---------------------------------------------------------------------------

const Skeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export const VehicleDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  useTranslation(); // keep i18n context active (QualificationBadge uses it)
  const { user } = useAuth();

  const isSuperAdmin = user?.isSuperAdmin === true || user?.role === 'super_admin';
  const canViewGps =
    isSuperAdmin ||
    user?.role === 'driver' ||
    user?.company?.gps_data_visible_to_company_users === true;

  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [vehicleLoading, setVehicleLoading] = useState(true);
  const [vehicleError, setVehicleError] = useState<string | null>(null);

  const [obdHealth, setObdHealth] = useState<OBDHealth | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [showHealthSection, setShowHealthSection] = useState(false);

  const [trips, setTrips] = useState<Trip[]>([]);
  const [tripsLoading, setTripsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const LIMIT = 10;

  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);

  const loadVehicle = useCallback(async () => {
    if (!id) return;
    setVehicleLoading(true);
    setVehicleError(null);
    try {
      const data = await vehiclesApi.getVehicle(id);
      setVehicle(data);
    } catch {
      setVehicleError('Impossible de charger le véhicule.');
    } finally {
      setVehicleLoading(false);
    }
  }, [id]);

  const loadObdHealth = useCallback(async () => {
    if (!id || !isSuperAdmin) return;
    setHealthLoading(true);
    try {
      const data = await vehiclesApi.getVehicleHealth(id);
      setObdHealth(data);
      setShowHealthSection(true);
    } catch (err: any) {
      const status = err?.response?.status;
      // 403 = not authorised; silently hide the section
      if (status !== 403) {
        setShowHealthSection(true); // show section with "no data" message
      }
    } finally {
      setHealthLoading(false);
    }
  }, [id, isSuperAdmin]);

  const loadTrips = useCallback(async () => {
    if (!id) return;
    setTripsLoading(true);
    try {
      const result = await tripsApi.getTrips({ vehicleId: id, page, limit: LIMIT });
      setTrips(Array.isArray(result.data) ? result.data : []);
      setTotalPages(result.pagination?.totalPages ?? 1);
    } catch {
      setTrips([]);
    } finally {
      setTripsLoading(false);
    }
  }, [id, page]);

  useEffect(() => {
    loadVehicle();
    loadObdHealth();
  }, [loadVehicle, loadObdHealth]);

  useEffect(() => {
    loadTrips();
  }, [loadTrips]);

  const getDriverName = (v: Vehicle): string => {
    if (!v.assigned_driver) return '—';
    if (typeof v.assigned_driver === 'object') {
      return (v.assigned_driver as User).name || '—';
    }
    return '—';
  };

  const getStatusBadge = (status: Vehicle['status']) => {
    const map: Record<Vehicle['status'], string> = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-600',
      maintenance: 'bg-yellow-100 text-yellow-800',
    };
    const labels: Record<Vehicle['status'], string> = {
      active: 'Actif',
      inactive: 'Inactif',
      maintenance: 'Maintenance',
    };
    return (
      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${map[status]}`}>
        {labels[status]}
      </span>
    );
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Retour
        </button>

        {/* Vehicle info card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {vehicleLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-32" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} className="h-12" />
                ))}
              </div>
            </div>
          ) : vehicleError ? (
            <div className="text-center py-8">
              <p className="text-red-600">{vehicleError}</p>
              <button
                onClick={loadVehicle}
                className="mt-3 text-sm text-primary-600 hover:underline"
              >
                Réessayer
              </button>
            </div>
          ) : vehicle ? (
            <div>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="text-2xl font-bold text-gray-900">{vehicle.plate_number}</h1>
                    {getStatusBadge(vehicle.status)}
                  </div>
                  <p className="text-gray-500 mt-1">
                    {vehicle.brand} {vehicle.model}
                    {vehicle.year ? ` · ${vehicle.year}` : ''}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <InfoField label="Marque / Modèle" value={`${vehicle.brand} ${vehicle.model}`} />
                <InfoField label="Année" value={vehicle.year?.toString() ?? '—'} />
                <InfoField
                  label="VIN"
                  value={vehicle.vin ? <span className="font-mono text-xs">{vehicle.vin}</span> : '—'}
                />
                <InfoField label="Conducteur assigné" value={getDriverName(vehicle)} />
                <InfoField
                  label="IMEI OBD"
                  value={
                    vehicle.obd_device_id ? (
                      <span className="font-mono text-xs">{vehicle.obd_device_id}</span>
                    ) : (
                      '—'
                    )
                  }
                />
                <InfoField label="Type OBD" value={vehicle.obd_device_type || '—'} />
                <InfoField
                  label="Kilométrage"
                  value={
                    <OdometerDisplay
                      value={vehicle.current_odometer}
                      source={vehicle.odometer_source}
                      updatedAt={vehicle.odometer_updated_at}
                    />
                  }
                />
              </div>
            </div>
          ) : null}
        </div>

        {/* OBD Health card — super_admin only */}
        {isSuperAdmin && showHealthSection && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Santé OBD
            </h2>

            {healthLoading ? (
              <div className="space-y-2">
                {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-9" />)}
              </div>
            ) : obdHealth ? (
              <div>
                <div className="divide-y divide-gray-100">
                  <MetricRow label="Tension batterie">
                    <BatteryValue voltage={obdHealth.battery_voltage} />
                  </MetricRow>
                  <MetricRow label="Température liquide de refroidissement">
                    <CoolantValue temp={obdHealth.coolant_temp} />
                  </MetricRow>
                  <MetricRow label="Température huile moteur">
                    <OilTempValue temp={obdHealth.engine_oil_temp} />
                  </MetricRow>
                  <MetricRow label="Niveau carburant">
                    <FuelBar level={obdHealth.fuel_level} />
                  </MetricRow>
                  <MetricRow label="Codes défaut (DTC)">
                    <DtcValue dtcCount={obdHealth.dtc_count} dtcCodes={obdHealth.dtc_codes} />
                  </MetricRow>
                  <MetricRow label="Distance avant entretien">
                    <ServiceDistanceValue km={obdHealth.service_distance} />
                  </MetricRow>
                </div>
                {obdHealth.updated_at && (
                  <p className="text-xs text-gray-400 mt-3 text-right">
                    Mise à jour : {formatDate(obdHealth.updated_at)} à {formatTime(obdHealth.updated_at)}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500 py-4 text-center">
                Aucune donnée de santé disponible
              </p>
            )}
          </div>
        )}

        {/* Smartcar integration panel */}
        {vehicle && <SmartcarPanel vehicleId={vehicle._id} />}

        {/* Trips table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">Trajets</h2>
          </div>

          {tripsLoading ? (
            <div className="p-6 space-y-3">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : trips.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-5xl mb-3">🚗</div>
              <p className="text-gray-500">Aucun trajet pour ce véhicule</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Durée
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Distance
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Qualification
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {trips.map((trip) => (
                    <tr key={trip._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatDate(trip.start_time)}
                        </div>
                        <div className="text-xs text-gray-500">{formatTime(trip.start_time)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {formatDuration(trip.duration_minutes)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm text-gray-700">{formatDistance(trip.distance_km)}</span>
                          {trip.distance_source === 'gps' && (
                            <span
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700"
                              title="Distance calculée par GPS"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              GPS
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <QualificationBadge type={trip.qualification} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => setSelectedTrip(trip)}
                          className="text-gray-400 hover:text-primary-600 transition-colors"
                          title="Voir le détail"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!tripsLoading && totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Page {page} sur {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Précédent
                </button>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Trip detail modal */}
      {selectedTrip && (
        <TripDetailModal
          trip={selectedTrip}
          canViewGps={canViewGps}
          onClose={() => setSelectedTrip(null)}
        />
      )}
    </Layout>
  );
};

// ---------------------------------------------------------------------------
// Small helper component
// ---------------------------------------------------------------------------

const InfoField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="bg-gray-50 rounded-lg px-4 py-3">
    <p className="text-xs text-gray-500 mb-0.5">{label}</p>
    <p className="text-sm font-medium text-gray-900">{value}</p>
  </div>
);
